import { useEffect, useMemo, useState } from "react";
import { createFileRoute, Link } from "@tanstack/react-router";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { Check, ChevronLeft, ChevronRight, ExternalLink, Pencil, Plus, Trash2, X } from "lucide-react";
import { toLocalDT, toSlug } from "@/components/admin/PostForm";
import { Select } from "@/components/admin/Select";
import { useConfirm } from "@/components/admin/ConfirmDialog";

export const Route = createFileRoute("/_authenticated/admin/")({
  component: AdminPosts,
});

type Row = {
  id: string;
  slug: string;
  h1_title: string;
  status: string;
  published_at: string | null;
  category_id: string | null;
  content_html: string | null;
  cover_image_url: string | null;
  categories: { name: string } | null;
};

const PER_PAGE = 10;

const STATUS_LABEL: Record<string, string> = {
  draft: "Rascunho",
  scheduled: "Agendado",
  published: "Publicado",
  trash: "Lixeira",
};

function AdminPosts() {
  const [rows, setRows] = useState<Row[]>([]);
  const [cats, setCats] = useState<{ id: string; name: string }[]>([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [status, setStatus] = useState("all");
  const [category, setCategory] = useState("all");
  const [from, setFrom] = useState("");
  const [to, setTo] = useState("");
  const [page, setPage] = useState(1);
  const { confirm, confirmDialog } = useConfirm();

  async function load() {
    setLoading(true);
    const [{ data }, { data: c }] = await Promise.all([
      supabase
        .from("posts")
        .select("id,slug,h1_title,status,published_at,category_id,content_html,cover_image_url,categories(name)")
        .is("deleted_at", null)
        .order("published_at", { ascending: false, nullsFirst: false }),
      supabase.from("categories").select("id,name").order("sort_order"),
    ]);
    setRows((data ?? []) as unknown as Row[]);
    setCats((c ?? []) as { id: string; name: string }[]);
    setLoading(false);
  }

  useEffect(() => {
    void load();
  }, []);

  const filtered = useMemo(
    () =>
      rows.filter((r) => {
        if (q && !(r.h1_title + r.slug).toLowerCase().includes(q.toLowerCase())) return false;
        if (status !== "all" && r.status !== status) return false;
        if (category !== "all" && r.category_id !== category) return false;
        if (from && (!r.published_at || new Date(r.published_at) < new Date(from))) return false;
        if (to && (!r.published_at || new Date(r.published_at) > new Date(`${to}T23:59:59`))) return false;
        return true;
      }),
    [rows, q, status, category, from, to],
  );

  const pages = Math.max(1, Math.ceil(filtered.length / PER_PAGE));
  const current = Math.min(page, pages);
  const visible = filtered.slice((current - 1) * PER_PAGE, current * PER_PAGE);

  async function trash(row: Row) {
    const ok = await confirm({
      title: "Mover artigo para a lixeira?",
      description: `"${row.h1_title}" sairá do site e poderá ser restaurado na lixeira.`,
      confirmLabel: "Mover para a lixeira",
    });
    if (!ok) return;
    const { error } = await supabase.from("posts").update({ deleted_at: new Date().toISOString() }).eq("id", row.id);
    if (error) return toast.error(error.message);
    toast.success("Artigo movido para a lixeira.");
    void load();
  }

  return (
    <div className="kk-cms__page">
      <div className="kk-cms__head">
        <div>
          <h1>Artigos</h1>
          <p>
            {filtered.length} artigo{filtered.length === 1 ? "" : "s"} · página {current} de {pages}
          </p>
        </div>
        <Link to="/admin/artigos/$id" params={{ id: "novo" }} className="kk-cms__btn">
          <Plus size={15} /> Novo artigo
        </Link>
      </div>

      <div className="kk-cms__filters">
        <input
          type="text"
          placeholder="Buscar por título ou slug…"
          value={q}
          onChange={(e) => {
            setQ(e.target.value);
            setPage(1);
          }}
        />
        <Select
          value={status}
          onChange={(x) => { setStatus(x); setPage(1); }}
          options={[
            { value: "all", label: "Todos os status" },
            { value: "published", label: "Publicado" },
            { value: "scheduled", label: "Agendado" },
            { value: "draft", label: "Rascunho" },
          ]}
        />
        <Select
          value={category}
          onChange={(x) => { setCategory(x); setPage(1); }}
          options={[{ value: "all", label: "Todas as categorias" }, ...cats.map((c) => ({ value: c.id, label: c.name }))]}
        />
        <input type="date" value={from} onChange={(e) => { setFrom(e.target.value); setPage(1); }} />
        <input type="date" value={to} onChange={(e) => { setTo(e.target.value); setPage(1); }} />
      </div>

      <div className="kk-cms__table-wrap">
        <table className="kk-cms__table">
          <thead>
            <tr>
              <th>Título</th>
              <th>Categoria</th>
              <th>Status</th>
              <th>Publicado</th>
              <th style={{ textAlign: "right" }}>Ações</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr>
                <td colSpan={5} className="kk-cms__empty">
                  Carregando…
                </td>
              </tr>
            ) : null}
            {!loading && !visible.length ? (
              <tr>
                <td colSpan={5} className="kk-cms__empty">
                  Nenhum artigo encontrado.
                </td>
              </tr>
            ) : null}
            {visible.map((row) => (
              <PostRow key={row.id} row={row} cats={cats} onChanged={load} onTrash={trash} />
            ))}
          </tbody>
        </table>
      </div>

      <div className="kk-cms__pager">
        <button type="button" disabled={current <= 1} onClick={() => setPage(current - 1)}>
          <ChevronLeft size={14} /> Anterior
        </button>
        <span>
          Página {current} de {pages}
        </span>
        <button type="button" disabled={current >= pages} onClick={() => setPage(current + 1)}>
          Próxima <ChevronRight size={14} />
        </button>
      </div>
      {confirmDialog}
    </div>
  );
}

function PostRow({
  row,
  cats,
  onChanged,
  onTrash,
}: {
  row: Row;
  cats: { id: string; name: string }[];
  onChanged: () => void;
  onTrash: (row: Row) => void;
}) {
  const [editing, setEditing] = useState(false);
  const [h1, setH1] = useState(row.h1_title);
  const [slug, setSlug] = useState(row.slug);
  const [status, setStatus] = useState(row.status);
  const [cat, setCat] = useState(row.category_id ?? "");
  const [dt, setDt] = useState(toLocalDT(row.published_at));

  async function save() {
    if (status !== "draft") {
      const text = (row.content_html ?? "").replace(/<[^>]*>/g, "").replace(/&nbsp;/g, " ").trim();
      if (!text) return toast.error("O artigo precisa ter conteúdo no texto para ser publicado.");
      if (!row.cover_image_url) return toast.error("O artigo precisa de uma imagem de capa (miniatura) para ser publicado.");
    }
    const { error } = await supabase
      .from("posts")
      .update({
        h1_title: h1,
        slug: toSlug(slug),
        status: status as "draft" | "published" | "scheduled",
        category_id: cat || null,
        published_at: dt ? new Date(dt).toISOString() : null,
      })
      .eq("id", row.id);
    if (error) return toast.error(error.message);
    toast.success("Artigo atualizado.");
    setEditing(false);
    onChanged();
  }

  if (editing) {
    return (
      <tr>
        <td>
          <input type="text" value={h1} onChange={(e) => setH1(e.target.value)} />
          <input type="text" value={slug} onChange={(e) => setSlug(e.target.value)} style={{ marginTop: 6, fontSize: 12 }} />
        </td>
        <td>
          <Select
            className="kk-select--sm"
            value={cat}
            onChange={setCat}
            options={[{ value: "", label: "Sem categoria" }, ...cats.map((c) => ({ value: c.id, label: c.name }))]}
          />
        </td>
        <td>
          <Select
            className="kk-select--sm"
            value={status}
            onChange={setStatus}
            options={[
              { value: "draft", label: "Rascunho" },
              { value: "scheduled", label: "Agendado" },
              { value: "published", label: "Publicado" },
            ]}
          />
        </td>
        <td>
          <input type="datetime-local" value={dt} onChange={(e) => setDt(e.target.value)} />
        </td>
        <td>
          <div className="kk-cms__row-actions">
            <button type="button" className="kk-icon-btn kk-icon-btn--ok" onClick={save} title="Salvar">
              <Check size={16} />
            </button>
            <button type="button" className="kk-icon-btn" onClick={() => setEditing(false)} title="Cancelar">
              <X size={16} />
            </button>
          </div>
        </td>
      </tr>
    );
  }

  return (
    <tr>
      <td>
        <Link to="/admin/artigos/$id" params={{ id: row.id }} className="kk-cms__title-link">
          {row.h1_title}
        </Link>
        <span className="kk-cms__slug">/{row.slug}</span>
      </td>
      <td>{row.categories?.name ?? "—"}</td>
      <td>
        <span className={`kk-badge kk-badge--${row.status}`}>{STATUS_LABEL[row.status] ?? row.status}</span>
      </td>
      <td>{row.published_at ? new Date(row.published_at).toLocaleDateString("pt-BR") : "—"}</td>
      <td>
        <div className="kk-cms__row-actions">
          <button type="button" className="kk-icon-btn" title="Edição rápida" onClick={() => setEditing(true)}>
            <Pencil size={16} />
          </button>
          {row.status === "published" ? (
            <a href={`/${row.slug}`} target="_blank" rel="noopener noreferrer" className="kk-icon-btn" title="Ver no site">
              <ExternalLink size={16} />
            </a>
          ) : null}
          <button type="button" className="kk-icon-btn kk-icon-btn--danger" title="Mover para a lixeira" onClick={() => onTrash(row)}>
            <Trash2 size={16} />
          </button>
        </div>
      </td>
    </tr>
  );
}
